import { useEffect, useState } from "react";
import { retrieveAssetChildren } from "../api";
import VisibilityIcon from "@mui/icons-material/Visibility";

// Hierarchy color scheme: readable, good contrast in both themes
const hierarchyColors = {
  dark: {
    pipeBorder: "1px solid rgba(148, 163, 184, 0.25)",
    tag: "#93c5fd",
    value: "#e2e8f0",
    label: "#94a3b8",
    badgeBg: "rgba(56, 189, 248, 0.2)",
    badgeText: "#7dd3fc",
    btn: "#A0C46D",
    btnBorder: "rgba(160, 196, 109, 0.5)",
  },
  light: {
    pipeBorder: "1px solid #cbd5e1",
    tag: "#0369a1",
    value: "#334155",
    label: "#64748b",
    badgeBg: "#e0f2fe",
    badgeText: "#0369a1",
    btn: "#4d7c2c",
    btnBorder: "rgba(100, 140, 60, 0.5)",
  },
};

const pipeBorder = (isDark) => (hierarchyColors[isDark ? "dark" : "light"] || hierarchyColors.light).pipeBorder;

function getStyles(isDark) {
  const c = hierarchyColors[isDark ? "dark" : "light"] || hierarchyColors.light;
  return {
    rootLabelContainer: {},
    labelContainer: {
      display: "grid",
      gridTemplateColumns: "148px 168px 96px auto",
      alignItems: "center",
      gap: "0 8px",
      padding: 0,
      whiteSpace: "nowrap",
      width: "100%",
      minWidth: 0,
    },
    cell: {
      display: "flex",
      alignItems: "center",
      gap: "2px",
      minWidth: 0,
      paddingLeft: "6px",
      borderLeft: pipeBorder(isDark),
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    cellFirst: {
      paddingLeft: 0,
      borderLeft: "none",
    },
    cellQuantity: {
      overflow: "visible",
      minWidth: 0,
    },
    cellLast: {
      marginLeft: "auto",
      marginRight: "auto",
    },
    valueWrap: {
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      minWidth: 0,
    },
    tag: {
      color: c.tag,
      fontWeight: 600,
      fontSize: "0.65rem",
    },
    value: {
      color: c.value,
      marginLeft: "2px",
      fontSize: "0.65rem",
    },
    label: {
      color: c.label,
      fontWeight: 500,
      fontSize: "0.65rem",
    },
    viewSnapshotBtn: {
      color: c.btn,
      backgroundColor: "transparent",
      cursor: "pointer",
      display: "inline-flex",
      alignItems: "center",
      gap: "2px",
      padding: "2px 6px",
      borderRadius: "4px",
      border: `1px solid ${c.btnBorder}`,
      transition: "background-color 0.2s ease, border-color 0.2s ease",
      fontSize: "0.65rem",
      fontWeight: 600,
      flexShrink: 0,
      minHeight: "22px",
    },
    badge: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      minWidth: "18px",
      padding: "1px 4px",
      borderRadius: "4px",
      backgroundColor: c.badgeBg,
      color: c.badgeText,
      fontSize: "0.65rem",
      fontWeight: 600,
      marginLeft: "2px",
      flexShrink: 0,
      fontVariantNumeric: "tabular-nums",
    },
  };
}

export function useTreeView(props) {
  const {
    apiUrl,
    assetData,
    assetId,
    facilities,
    history,
    isDark = false,
    mounted,
    organizationId,
    token,
    zones,
  } = props;

  const styles = getStyles(isDark);
  const [treeView, setTreeView] = useState();

  // Handles the Hierarchy Tab and Hierarchy Status Table
  useEffect(() => {
    const viewSnapshot = (assetId) => {
      history.push(`/assetSnapshot/${assetId}`);
      // history.go(0);
    };

    const recursiveFetch = (assetId) => {
      const fetchChildren = async (assetId) => {
        const children = await retrieveAssetChildren(
          { organizationId, apiUrl, token, zones, facilities },
          assetId
        );

        const results = children.map(async (child) => {
          child.children = await fetchChildren(child.id);
          return child;
        });

        return await Promise.all(results).then((completed) => {
          return completed;
        });
      };

      const results = fetchChildren(assetId);

      return results;
    };

    const collectAllNodeIds = (nodes) => {
      if (!nodes || !Array.isArray(nodes)) return [];
      return nodes.flatMap((n) => [n.id, ...collectAllNodeIds(n.children)]);
    };

    const generateTreeView = async () => {
      // if assetData has ancestors, i.e., it is itself a child, then start the root of the tree at the last item in its ancestor list,
      // i.e., it's progenitor
      const results = await recursiveFetch(
        assetData.ancestors?.length
          ? assetData.ancestors[assetData.ancestors.length - 1]
          : assetData.assetId
      ).then(async (children) => {
        const recurseAndCount = async (node) => {
          const results = node.map(async (child) => {
            const label =
              assetData.assetId === child.id
                ? "Current Asset Tag: "
                : assetData.ancestors?.includes(child.id)
                ? "Parent Asset Tag: "
                : "Child Asset Tag: ";

            const showViewSnapshotIcon = assetData.assetId !== child.id;
            const isChildAsset = label === "Child Asset Tag: ";
            const childrenCount = child?.children?.length || 0;
            
            child.label = (
              <span style={styles.labelContainer}>
                <span style={{ ...styles.cell, ...styles.cellFirst }}>
                  <span style={styles.label}>{label}</span>
                  <span style={{ ...styles.tag, ...styles.valueWrap }} title={child.assetTag}>{child.assetTag}</span>
                </span>
                <span style={styles.cell}>
                  <span style={styles.label}>Last Event:</span>
                  <span style={{ ...styles.value, ...styles.valueWrap }} title={child.lastEvent}>{child.lastEvent}</span>
                </span>
                {isChildAsset ? (
                  <span style={{ ...styles.cell, ...styles.cellQuantity }}>
                    <span style={styles.label}>Quantity:</span>
                    <span style={styles.badge}>{child.quantityPicked ?? 0}</span>
                  </span>
                ) : (
                  <span style={{ ...styles.cell, ...styles.cellQuantity }}>
                    <span style={styles.label}>Children:</span>
                    <span style={styles.badge}>{childrenCount}</span>
                  </span>
                )}
                {showViewSnapshotIcon ? (
                  <span style={{ ...styles.cell, ...styles.cellLast }}>
                    <span
                      role="button"
                      tabIndex={0}
                      aria-label={`View snapshot for ${child.assetTag}`}
                      style={styles.viewSnapshotBtn}
                      onClick={(e) => { e.stopPropagation(); viewSnapshot(child.id); }}
                      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.stopPropagation(); viewSnapshot(child.id); } }}
                      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "rgba(160, 196, 109, 0.2)"; e.currentTarget.style.borderColor = "rgba(160, 196, 109, 0.6)"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = ""; e.currentTarget.style.borderColor = ""; }}
                    >
                      View Snapshot
                      <VisibilityIcon
                        sx={{ color: isDark ? "#A0C46D" : "#4d7c2c", fontSize: "0.7rem" }}
                      />
                    </span>
                  </span>
                ) : null}
              </span>
            );

            if (child?.children?.length > 0) {
              recurseAndCount(child.children);
            }
            return child;
          });

          return await Promise.all(results).then((completed) => {
            return completed;
          });
        };

        return await recurseAndCount(children).then((children) => {
          const rootAncestorSource =
            assetData?.ancestors?.length > 0 &&
            assetData?.ancestorList?.length > 0
              ? assetData?.ancestorList?.filter(
                  (ancestor) =>
                    ancestor.assetId ===
                    assetData.ancestors[assetData.ancestors.length - 1]
                )
              : [assetData];

          const rootAncestor = rootAncestorSource?.map((asset) => {
            const zone =
              zones &&
              asset?.zone &&
              asset.zone?.zoneId &&
              zones[asset.zone.zoneId]
                ? zones[asset.zone.zoneId].name
                : null;
            const binLocation =
              asset.zone && asset.zone.binLocation
                ? asset.zone.binLocation
                : null;
            // Only show facility if asset has no GPS coordinates
            // If asset has GPS coordinates, it's outside a facility
            const hasGpsCoordinates = asset.latitude && asset.longitude && 
              (asset.latitude !== 0 || asset.longitude !== 0);
            
            return {
              assetTag: asset.tag,
              ancestorList: asset.ancestorList || [],
              ancestors: asset.ancestors || [],
              binLocation,
              facility: hasGpsCoordinates 
                ? "" 
                : (facilities[asset.facilityId] ? facilities[asset.facilityId].name : ""),
              latitude: asset.latitude,
              longitude: asset.longitude,
              id: asset.assetId,
              lastEvent: asset.lastEvent,
              parentId: asset.parentId,
              quantityNeeded: asset.quantity
                ? asset.quantity.quantityNeeded
                : null,
              quantityPicked: asset.quantity
                ? asset.quantity.quantityPicked
                : null,
              zone,
            };
          })[0];

          const rootAncestorChildrenCount = Array.isArray(children)
            ? children.length
            : 0;

          const rootAncestorTag =
            assetData.assetId === rootAncestor.id
              ? "Current Asset Tag: "
              : "Parent Asset Tag: ";

          const showViewSnapshotIcon = rootAncestor.id !== assetData.assetId;

          const rootAncestorLabel = rootAncestor ? (
            <span style={{ ...styles.labelContainer, ...styles.rootLabelContainer }}>
              <span style={{ ...styles.cell, ...styles.cellFirst }}>
                <span style={styles.label}>{rootAncestorTag}</span>
                <span style={{ ...styles.tag, ...styles.valueWrap }} title={rootAncestor.assetTag}>{rootAncestor.assetTag}</span>
              </span>
              <span style={styles.cell}>
                <span style={styles.label}>Last Event:</span>
                <span style={{ ...styles.value, ...styles.valueWrap }} title={rootAncestor.lastEvent}>{rootAncestor.lastEvent}</span>
              </span>
              <span style={{ ...styles.cell, ...styles.cellQuantity }}>
                <span style={styles.label}>Children:</span>
                <span style={styles.badge}>{rootAncestorChildrenCount}</span>
              </span>
              {showViewSnapshotIcon ? (
                <span style={{ ...styles.cell, ...styles.cellLast }}>
                  <span
                    role="button"
                    tabIndex={0}
                    aria-label={`View snapshot for ${rootAncestor.assetTag}`}
                    style={styles.viewSnapshotBtn}
                    onClick={(e) => { e.stopPropagation(); viewSnapshot(rootAncestor.id); }}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.stopPropagation(); viewSnapshot(rootAncestor.id); } }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "rgba(160, 196, 109, 0.2)"; e.currentTarget.style.borderColor = "rgba(160, 196, 109, 0.6)"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = ""; e.currentTarget.style.borderColor = ""; }}
                  >
                    View Snapshot
                    <VisibilityIcon
                      sx={{ color: isDark ? "#A0C46D" : "#4d7c2c", fontSize: "0.7rem" }}
                    />
                  </span>
                </span>
              ) : null}
            </span>
          ) : null;

          const allNodeIds = [rootAncestor.id, ...collectAllNodeIds(children)];
          return {
            id: rootAncestor.id,
            label: rootAncestorLabel,
            lastEvent: rootAncestor.lastEvent,
            children,
            defaultSelected: assetData.assetId,
            defaultExpanded: allNodeIds,
            complete: true,
          };
        });
      });

      return results;
    };

    if (mounted && assetData?.assetId) {
      generateTreeView().then((res) => {
        setTreeView((t) => {
          return { ...t, ...res, complete: true };
        });
      });
    }

    return () => {
      setTreeView((prevState) => {
        return {
          ...prevState,
          complete: false,
        };
      });
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    facilities,
    history,
    assetId,
    isDark,
    organizationId,
    apiUrl,
    token,
    zones,
    assetData,
    mounted,
  ]);

  return treeView;
}
